import { useMemo } from 'react';

import { AwardingV2ChartData } from 'api/useAnalytics';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis
} from 'recharts';

import { CHART_COLORS } from 'components/AnalyticsBox/utils';
import { ChartContainer } from 'components/ui/chart';

interface AwardingV2ChartProps {
  data: AwardingV2ChartData[];
}

export const AwardingV2Chart = ({ data }: AwardingV2ChartProps) => {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const endpoints = Array.from(new Set(data.map((item) => item.endpoint)));

    const timeFrames = Array.from(
      new Set(data.map((item) => item.timeFrame))
    ).sort();

    return timeFrames.map((timeFrame) => {
      const dataPoint: Record<string, string | number> = { timeFrame };

      endpoints.forEach((endpoint) => {
        const item = data.find(
          (d) => d.timeFrame === timeFrame && d.endpoint === endpoint
        );
        dataPoint[endpoint] = item?.successRatePercent ?? 0;
      });

      return dataPoint;
    });
  }, [data]);

  const endpoints = useMemo(() => {
    if (!data || data.length === 0) return [];
    return Array.from(new Set(data.map((item) => item.endpoint)));
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="w-full">
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-base-foreground mb-1">
            Success rate
          </h3>
          <p className="text-sm text-base-mutedForeground">Last 7 days</p>
        </div>
        <div className="min-h-[220px] max-h-[220px] w-full flex items-center justify-center text-base-mutedForeground">
          No data available
        </div>
      </div>
    );
  }

  const tickStyle = {
    fill: 'var(--base-muted-foreground)',
    fontSize: '10px',
    fontFamily: 'var(--typography-font-family-font-sans, Inter)',
    fontStyle: 'normal',
    fontWeight: 'var(--font-weight-normal, 400)'
  };

  const chartConfig = endpoints.reduce(
    (acc, endpoint, index) => {
      acc[endpoint] = {
        label: endpoint,
        color: CHART_COLORS[index % CHART_COLORS.length]
      };
      return acc;
    },
    {} as Record<string, { label: string; color: string }>
  );

  const yAxisTicks = [0, 50, 100];

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${month}/${day}`;
    } catch {
      // If date parsing fails, try to extract mm/dd from yyyy-mm-dd format
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[1]}/${parts[2]}`;
      }
      return dateString;
    }
  };

  const CustomTooltip = ({
    active,
    payload,
    coordinate
  }: TooltipProps<number, string>) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const data = payload[0].payload;
    const x = coordinate?.x ?? 0;
    const y = coordinate?.y ?? 0;

    return (
      <div
        style={{
          position: 'absolute',
          left: `${x}px`,
          top: `${y}px`,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
          backgroundColor: 'var(--base-popover)',
          border: '1px solid var(--base-border)',
          borderRadius: '4px',
          padding: '6px 8px',
          boxShadow: '0 2px 4px -1px rgba(0, 0, 0, 0.1)'
        }}
      >
        <div className="text-xs font-medium text-base-foreground mb-1">
          Time: {formatDate(data.timeFrame)}
        </div>
        <div className="space-y-0.5">
          {payload.map((entry, index) => (
            <div
              key={index}
              className="flex items-center gap-1 text-[10px]"
              style={{ color: entry.color }}
            >
              <div
                className="w-1 h-1 rounded-full"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-base-foreground">{entry.name}:</span>
              <span className="font-medium text-base-foreground">
                {typeof entry.value === 'number'
                  ? `${entry.value.toFixed(1)}%`
                  : entry.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-base-foreground mb-1">
          Success rate
        </h3>
        <p className="text-sm text-base-mutedForeground">Last 7 days</p>
      </div>
      <ChartContainer
        config={chartConfig}
        className="min-h-[220px] max-h-[220px] w-full"
      >
        <LineChart
          margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
          data={chartData}
        >
          <CartesianGrid vertical={false} stroke="var(--base-border)" />
          <XAxis
            dataKey="timeFrame"
            axisLine={false}
            tickLine={false}
            tick={tickStyle}
            tickFormatter={formatDate}
            dy={10}
          />
          <YAxis
            domain={[0, 100]}
            ticks={yAxisTicks}
            tickFormatter={(value) => `${value}%`}
            tick={tickStyle}
            axisLine={false}
            tickLine={false}
            tickMargin={8}
            allowDecimals={false}
            width={45}
            type="number"
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ stroke: 'var(--base-border)', strokeWidth: 2 }}
          />
          <Legend
            formatter={(value) => (
              <span className="text-xs text-base-foreground">{value}</span>
            )}
            iconSize={10}
            iconType="square"
            wrapperStyle={{ paddingTop: '24px' }}
          />
          {endpoints.map((endpoint, index) => (
            <Line
              key={endpoint}
              type="monotone"
              dataKey={endpoint}
              stroke={CHART_COLORS[index % CHART_COLORS.length]}
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 4,
                stroke: 'var(--base-white)',
                strokeWidth: 1,
                fill: CHART_COLORS[index % CHART_COLORS.length]
              }}
            />
          ))}
        </LineChart>
      </ChartContainer>
    </div>
  );
};
